import { ApiResponse, Pagination } from "interfaces/models/api";
import { InspectionContent } from "interfaces/models/inspectionContent";
import { InspectionTarget } from "interfaces/models/InspectionTarget";
import { InspectionType } from "interfaces/models/inspectionType";
import { axiosECS } from "services/baseAxios";
import { sleep } from "utils/common";
import {
  blackboardFormatApi,
  blackboardFrameApi,
  inspectionTargetApi,
} from "..";
import { getInspectionContents } from "./inspectionContentApi";
import { PATH_API } from "constants/api";
import { presignedAndDownloadFileS3 } from "utils/file";
import { PinService } from "services/pin";
import CacheService from "libs/cache-project/classes/CacheService";
import { InspectionGroupService } from "services/inspectionGroup";

const pathV2 = PATH_API.INSPECTION_TYPE;

interface GetInspectionTypeReq extends Pagination {
  bimFileId: string;
  name?: string;
  cursor?: string;
  shouldCache?: boolean;
  projectId?: string;
  bimVersionId?: string;
  cacheService?: CacheService;
}

export interface CreateInspectionTypeRes {
  inspectionType: InspectionType;
  inspectionTarget: InspectionTarget;
  inspectionContent: InspectionContent;
}

export interface CopyInspectionTypeRes {
  bimFileList: string[];
}

const DEFAULT_LIMIT = 100;

export const getInspectionType = async (
  params: GetInspectionTypeReq
): Promise<ApiResponse<InspectionType[]>> => {
  const { limit, ...rest } = params;

  return axiosECS.get(pathV2, {
    params: { limit: limit || DEFAULT_LIMIT, ...rest },
  });
};

export const getAllInspectionTypeWithCursor = async (
  _params: GetInspectionTypeReq
) => {
  const { shouldCache, projectId, bimVersionId, cacheService, ...params } =
    _params;
  const inspectionTypes: InspectionType[] = [];
  let cursor: string | undefined;
  do {
    const { data, pagination } = await getInspectionType({
      shouldCache,
      ...params,
      paging: "cursor",
      cursor,
    });
    cursor = pagination?.cursor;
    if (cacheService?.isCancel()) throw new Error();
    if (data === undefined) {
      // case has error -> we not need continue query
      return;
    }
    if (data.length) {
      inspectionTypes.push.apply(inspectionTypes, data);
    }
  } while (cursor);

  if (shouldCache) {
    await handleCachedInspectionTypes(
      inspectionTypes,
      projectId!,
      bimVersionId!,
      cacheService
    );
  }

  return inspectionTypes;
};

export const getInspectionTypeById = async (
  id: string,
  shouldCache?: boolean
): Promise<ApiResponse<InspectionType>> => {
  return axiosECS.get(`${pathV2}/${id}`, {
    params: { shouldCache },
  });
};

export const createInspectionType = async (
  inspectionType: InspectionType
): Promise<ApiResponse<CreateInspectionTypeRes>> => {
  return axiosECS.post(pathV2, inspectionType);
};

export const copyInspectionType = async (body: {
  inspectionTypeId: string;
  bimFiles: string[];
  copyName: string;
}): Promise<ApiResponse<CopyInspectionTypeRes>> => {
  return axiosECS.post(`${pathV2}/copy`, body);
};

export const updateInspectionType = async (
  inspectionType: Partial<InspectionType>
): Promise<ApiResponse<InspectionType>> => {
  return axiosECS.patch(pathV2, inspectionType);
};

export const deleteInspectionType = async (
  id: string
): Promise<ApiResponse<InspectionType>> => {
  return axiosECS.delete(pathV2, {
    data: { id },
  });
};

const handleCachedInspectionTypes = async (
  inspectionTypes: InspectionType[],
  projectId: string,
  bimVersionId: string,
  cacheService?: CacheService
) => {
  let requests: Promise<any>[] = [];
  for (let i = 0; i < inspectionTypes.length; i++) {
    if (cacheService?.isCancel()) throw new Error();
    const inspectionType: InspectionType = inspectionTypes?.[i];
    if (!inspectionType?.id) {
      continue;
    }

    requests.push(getInspectionTypeById(inspectionType.id, true));
    requests.push(
      InspectionGroupService.getInspectionGroupList({
        inspectionTypeId: inspectionType.id!,
        shouldCache: true,
      }, cacheService)
    );

    requests.push(
      PinService.getAllPins(
        {
          inspectionTypeId: inspectionType.id!,
          shouldCache: true,
        },
        cacheService
      )
    );
    requests.push(
      getInspectionContents(
        {
          inspectionTypeId: inspectionType.id,
          shouldCache: true,
        },
        cacheService
      )
    );
    requests.push(
      inspectionTargetApi.getCombinedObjectList(inspectionType.id!, true)
    );
    requests.push(
      inspectionTargetApi.getExclusionObjectList({
        inspectionTypeId: inspectionType.id!,
        shouldCache: true,
      })
    );

    requests.push(
      blackboardFormatApi
        .getBlackboardFormat(inspectionType.id!, true)
        .then(async (res) => {
          const blackboardFormatId = res?.data?.id;
          const layoutUrl = res?.data?.layoutUrl;
          const promises = [];
          if (layoutUrl) {
            promises.push(presignedAndDownloadFileS3(layoutUrl, true));
          }
          if (blackboardFormatId) {
            promises.push(
              blackboardFrameApi.getBlackboardFrameList(
                blackboardFormatId,
                true
              )
            );
          }

          return Promise.all(promises);
        })
    );

    if (requests.length >= 20) {
      await Promise.all(requests);
      await sleep(50);
      requests = [];
    }
  }

  await Promise.all(requests);
};
